import React from 'react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useDeleteBroadcastMessage } from '../../../hooks/use-delete-broadcast-message';
import { usePopUpConfirmation } from '@/features/shared/pop-up-confirmation/hooks';
import { useArchiveBroadcastMessage } from '../../../hooks/use-archive-broadcast-message';
import { useSetToActiveBroadcastMessage } from '../../../hooks/use-set-to-active-broadcast-message';
import { composeClassName } from '@/utils/util_helpers';
import { HeroIcons } from '@/components/design-system/atoms/icon';
import { Button } from '@/components/design-system/atoms/button';
import { MessageCardProps, BroadcastMessage } from '../../../types';

const MessageCard: React.FC<
  MessageCardProps & {
    onEdit: (message: BroadcastMessage) => void;
    refetch: () => void;
  }
> = ({ title, message, startDate, status, onEdit, messageData, refetch }) => {
  const { t } = useTranslation()!;
  const { openPopUpConfirmation } = usePopUpConfirmation()!;
  const { deleteMessage, isLoading: isDeleting } = useDeleteBroadcastMessage();
  const { archiveMessage, isLoading: isArchiving } = useArchiveBroadcastMessage();
  const { setToActive, isLoading: isSettingToActive } = useSetToActiveBroadcastMessage();

  const handleArchive = () => {
    openPopUpConfirmation({
      type: 'default',
      title: t('js.features.admin.broadcast_messages.archive_broadcast_message'),
      description: t('js.features.admin.broadcast_messages.archive_broadcast_message_description'),
      confirmLabel: t('js.features.admin.broadcast_messages.archive'),
      cancelLabel: t('js.features.admin.broadcast_messages.keep_it_active'),
      onConfirm: () => {
        archiveMessage(messageData.id);
        refetch();
      },
    });
  };

  const handleSetToActive = () => {
    openPopUpConfirmation({
      type: 'default',
      title: t('js.features.admin.broadcast_messages.set_to_active'),
      description: t('js.features.admin.broadcast_messages.set_to_active_description'),
      confirmLabel: t('js.features.admin.broadcast_messages.set_to_active'),
      cancelLabel: t('js.features.admin.broadcast_messages.keep_it'),
      onConfirm: () => {
        setToActive(messageData.id);
        refetch();
      },
    });
  };

  return (
    <div className="flex w-full flex-col rounded-lg bg-bg-primaryMedium p-4">
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-2">
          <div className="flex items-start justify-between gap-2">
            <div className="flex flex-1 flex-col gap-1">
              <div className="flex flex-1 flex-col gap-2">
                <h3 className="text-2xl font-bold text-text-primaryHeavy">{title}</h3>
                <p className="line-clamp-2 min-h-[42px] text-body text-text-primaryMedium">
                  {message}
                </p>
              </div>

              <div className="flex flex-1 flex-wrap gap-2">
                {messageData.all_colocations ? (
                  <div className="flex flex-wrap items-center gap-2 rounded-full border border-solid border-border-primaryMedium bg-bg-primaryMedium px-2 py-1">
                    <HeroIcons name="home" className="text-text-primaryMedium" size={18} />
                    <span className="text-body text-medium text-text-primaryMedium">
                      {t('js.features.admin.broadcast_messages.form.all_houses')}
                    </span>
                  </div>
                ) : (
                  messageData.colocations.map((colocation) => (
                    <div
                      key={colocation.id}
                      className="flex flex-wrap items-center gap-2 rounded-full border border-solid border-border-primaryMedium bg-bg-primaryMedium px-2 py-1"
                    >
                      <HeroIcons name="home" className="text-text-primaryMedium" size={18} />
                      <span className="text-body text-medium text-text-primaryMedium">
                        {colocation.name}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
            {messageData.image_url && (
              <div className="flex h-[56px] w-[56px] items-center justify-center gap-2">
                <img
                  src={messageData.image_url}
                  alt="Broadcast Message Image"
                  className="h-full w-full rounded-md object-cover"
                />
              </div>
            )}
          </div>
          {messageData.click_action !== 'none' && (
            <span className="text-body text-medium text-text-primaryLight">
              OnTap: URL: {messageData.button_url}
            </span>
          )}
        </div>
        <div className="h-[1px] w-full bg-border-primaryMedium" />
        <div className="flex flex-col items-start gap-2">
          <div className="flex w-full flex-wrap items-center justify-between gap-2">
            <span
              className={composeClassName(
                'rounded-full px-2 py-1 text-body capitalize leading-[21px]',
                status === 'active' &&
                  'border border-solid border-[#1B998B] bg-[#E8F5F3] text-text-success',
                status === 'scheduled' &&
                  'border border-solid border-[#FECE1F] bg-[#FFFAE9] text-[#9B7E13]',
                status === 'archived' &&
                  'border border-solid border-[#CED4DA] bg-[#F8F9FA] text-text-primary'
              )}
            >
              {status}
            </span>
            <div className="flex items-start gap-2">
              <Button
                iconName="edit"
                variant="ghost"
                size="small"
                className="bg-[#F3F4F5]"
                onClick={() => onEdit(messageData)}
              >
                {t('js.features.admin.broadcast_messages.edit')}
              </Button>
              {status === 'active' && (
                <Button
                  variant="ghost"
                  size="small"
                  className="bg-[#F3F4F5]"
                  disabled={isArchiving}
                  onClick={handleArchive}
                >
                  {t('js.features.admin.broadcast_messages.archive')}
                </Button>
              )}
              {status === 'archived' && (
                <Button
                  variant="ghost"
                  size="small"
                  className="bg-[#1B998B] text-white hover:bg-[#E8F5F3] hover:text-text-primary disabled:bg-[#CED4DA] disabled:text-text-primary disabled:opacity-25"
                  disabled={isSettingToActive || messageData.is_expired}
                  onClick={handleSetToActive}
                >
                  {t('js.features.admin.broadcast_messages.set_to_active')}
                </Button>
              )}
              <Button
                variant="warning"
                size="small"
                onClick={() => {
                  openPopUpConfirmation({
                    type: 'warning',
                    title: t('js.features.admin.broadcast_messages.delete_confirmation_title'),
                    description: t(
                      'js.features.admin.broadcast_messages.delete_confirmation_description'
                    ),
                    confirmLabel: t('js.features.admin.broadcast_messages.delete_button'),
                    cancelLabel: t('js.features.admin.broadcast_messages.keep_it'),
                    onConfirm: () => {
                      deleteMessage(messageData.id);
                    },
                  });
                }}
                disabled={isDeleting}
              >
                {t('js.features.admin.broadcast_messages.delete')}
              </Button>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[12px] text-text-primaryLight">
              {status === 'scheduled'
                ? t('js.features.admin.broadcast_messages.scheduled_at', {
                    date: format(startDate, 'dd/LLL, HH:mm'),
                  })
                : t('js.features.admin.broadcast_messages.broadcasted_at', {
                    date: format(startDate, 'dd/LLL, HH:mm'),
                  })}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export { MessageCard };
